import React, { lazy, Suspense, memo } from 'react';
import { Typography, Spin } from 'antd';
import BaseResultDisplay from './BaseResultDisplay';
import componentRegistry from '../componentRegistry';
import EcoliExpressionResult from '../protein/advanced/EcoliExpressionResult';
import DefaultResultDisplay from './DefaultResultDisplay';
import { ResultComponentProps } from './types';

// 懒加载较重的组件
const LazyChargeProfileResult = lazy(() => import('../protein/basic/ChargeProfileResult'));

const { Text } = Typography;

// 懒加载包装器组件
const LazyLoadWrapper = memo(({ children }: { children: React.ReactNode }) => {
  return (
    <Suspense fallback={
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin tip="正在加载结果组件..." />
      </div>
    }>
      {children}
    </Suspense>
  );
});

// 统一的结果展示组件 - 简化职责，只做组件路由
const ResultDisplay: React.FC<ResultComponentProps & { taskId: string; originalInput?: string }> = memo(({
  data,
  sequence,
  title,
  onDeleteResult,
  multiResults = false,
  taskId,
  resultIndex,
  onExportAllCSV,
  originalInput
}) => {
  // 如果数据看起来是pH电荷曲线格式，优先使用ChargeProfileResult
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null
      && 'ph' in data[0] && 'charge' in data[0]) {
    return (
      <LazyLoadWrapper>
        <LazyChargeProfileResult
          data={data}
          sequence={sequence}
          title={title}
          onDeleteResult={onDeleteResult}
          multiResults={multiResults}
          resultIndex={resultIndex}
          onExportAllCSV={onExportAllCSV}
          originalInput={originalInput}
        />
      </LazyLoadWrapper>
    );
  }

  // 优先处理统一格式数据结构：{ total_sequences, results: [...], artifacts, meta }
  if (data && typeof data === 'object' && 'total_sequences' in (data as any) && 'results' in (data as any)) {
    const unifiedData = data as any;
    const results = unifiedData.results;
    const meta = unifiedData.meta || {};
    const service = meta.service;
    
    if (Array.isArray(results) && results.length > 0) {
      const first = results[0];
      
      // 根据服务类型或结果特征路由到专用组件
      if (service === 'tm_prediction' || (first.result && ('tm_regions_count' in first.result || 'annotation' in first.result))) {
        const TMPredictionResult = componentRegistry.get('tm_prediction');
        if (TMPredictionResult) {
          return (
            <LazyLoadWrapper>
              <TMPredictionResult
                data={data}
                sequence={sequence}
                title={title}
                onDeleteResult={onDeleteResult}
                multiResults={multiResults}
                resultIndex={resultIndex}
                onExportAllCSV={onExportAllCSV}
                originalInput={originalInput}
              />
            </LazyLoadWrapper>
          );
        }
      }
      
      // 信号肽预测
      if (service === 'signal_prediction' || (first.result && ('signal_peptide' in first.result || 'cleavage_site' in first.result))) {
        const SignalPeptideResult = componentRegistry.get('signal_prediction');
        if (SignalPeptideResult) {
          return (
            <LazyLoadWrapper>
              <SignalPeptideResult
                data={data}
                sequence={sequence}
                title={title}
                onDeleteResult={onDeleteResult}
                multiResults={multiResults}
                resultIndex={resultIndex}
                onExportAllCSV={onExportAllCSV}
                originalInput={originalInput}
              />
            </LazyLoadWrapper>
          );
        }
      }
      
      // 无序区域预测
      if (service === 'disorder' || (first.result && ('disorder_regions' in first.result || 'disorder_score' in first.result))) {
        const DisorderPredictionResult = componentRegistry.get('disorder');
        if (DisorderPredictionResult) {
          return (
            <LazyLoadWrapper>
              <DisorderPredictionResult
                data={data}
                sequence={sequence}
                title={title}
                onDeleteResult={onDeleteResult}
                multiResults={multiResults}
                resultIndex={resultIndex}
                onExportAllCSV={onExportAllCSV}
                originalInput={originalInput}
              />
            </LazyLoadWrapper>
          );
        }
      }
      
      // PTM预测
      if (service === 'ptm' || (first.result && 'ptm_type' in first.result) || 'ptm_type' in unifiedData) {
        const PTMPredictionResult = componentRegistry.get('ptm_prediction');
        if (PTMPredictionResult) {
          return (
            <LazyLoadWrapper>
              <PTMPredictionResult
                data={data}
                sequence={sequence}
                title={title}
                onDeleteResult={onDeleteResult}
                multiResults={multiResults}
                resultIndex={resultIndex}
                onExportAllCSV={onExportAllCSV}
                originalInput={originalInput}
              />
            </LazyLoadWrapper>
          );
        }
      }
      
      // E.coli表达量预测
      if (service === 'ecoli_expression' || (first.result && 'expression_score' in first.result)) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[ResultDisplay] Using EcoliExpressionResult by unified format');
        }
        return (
          <LazyLoadWrapper>
            <EcoliExpressionResult
              data={data}
              sequence={sequence}
              title={title}
              onDeleteResult={onDeleteResult}
              multiResults={multiResults}
              resultIndex={resultIndex}
              onExportAllCSV={onExportAllCSV}
              originalInput={originalInput}
            />
          </LazyLoadWrapper>
        );
      }
    }
  }

  // 简化组件查找 - 直接委托给TaskResultRenderer
  // ResultDisplay的职责：只做基本的数据检查和格式化，组件查找交给TaskResultRenderer
  
  // 如果数据格式不正确，使用默认展示
  if (!data) {
    return (
      <DefaultResultDisplay
        data={data}
        title={title || `${taskId} 分析结果`}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        resultIndex={resultIndex}
      />
    );
  }
  
  // 委托给TaskResultRenderer进行智能组件查找和渲染
  // 这样避免了两个组件的职责重叠
  const taskLikeData = {
    id: taskId,
    service_name: taskId.split('/')[0] || '',
    task_type: '',
    handler_name: taskId.split('/').pop() || taskId,
    status: 'completed',
    result: data,
    sequence,
    title
  };
  
  // 导入TaskResultRenderer（避免循环导入）
  const TaskResultRenderer = React.lazy(() => import('../TaskResultRenderer'));
  
  return (
    <LazyLoadWrapper>
      <TaskResultRenderer 
        task={taskLikeData}
        showRaw={false}
        onDeleteResult={onDeleteResult}
      />
    </LazyLoadWrapper>
  );
});

export default ResultDisplay; 